/* ============================================================
   Ami — the storefront assistant as a native element (Sep 2026).
   Plan: s2-portal-v2/docs/2026-09-21-ami-platform-plan.md §4.1–4.4, §4.6,
   workstream D. Wire contract: services/help/chat/stream-frames.ts and
   services/help/blocks/block-schemas.ts in the backend — the theme is written
   against those files, not the plan's tables.

   Defines <ami-chat> and two globals:
     window.AmiChat            — open(question, {surface}), close(), isOpen(), send(text)
     window.Studio2amHelpWidget — the embed.js shim (open/close/isOpen), so
                                 discover.js and any third-party host keep working.

   The element is created on the first open and appended to <body>. It reads
   its configuration off <bottom-rail> (sections/bottom-rail.liquid):
   data-ami-context (the page, from Liquid), data-ami-css (ami.css, injected
   here on first open so pages that never open the chat never fetch it),
   data-ami-api-host (the API host for the CORS routes used while degraded:
   the help site's chat routes, and its verify route when the proxy's own is
   unavailable) and data-ami-design-mode (the theme editor: never talk to
   the API).

   Transport (§4.2): POST /apps/discover/api/ami/chat through the app proxy,
   SSE, one `data:` line per frame. The first frame is `open`; if it has not
   arrived two seconds after the request went out the proxy is buffering, the
   request is abandoned and the turn degrades to the help site's CORS stream,
   then to its JSON route. A 404 means AMI_PLATFORM_ENABLED is off and
   degrades the same way. The choice is remembered for the session.

   Geometry and stacking are carried over from apps/help/public/embed.js:
   docked, the card is the rail capsule grown upward (same left, width and
   bottom edge, at z 29 in the rail's layer); below 1000px or with no room it
   is a full-screen modal. html.s2-ami-open is the rail's hook for both.
   ============================================================ */

(() => {
  'use strict';

  if (window.AmiChat) return;

  const PROXY_BASE = '/apps/discover/api/ami';
  const DEFAULT_API_HOST = 'https://api.studio2am.co';
  const HELP_HOST = 'https://help.studio2am.co';
  const ANCHOR_SELECTOR = '[data-s2-ami-anchor]';
  const OPEN_CLASS = 's2-ami-open';
  const COMPACT = window.matchMedia('(max-width: 999px)');

  const STORAGE_ID = 'ami_conversation_id';
  const STORAGE_AT = 'ami_conversation_at';
  const STORAGE_RESOLVED = 'ami_resolution_asked';
  const STORAGE_TRANSPORT = 'ami_transport';
  const CONVERSATION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
  /* A conversation verified by email and order number carries that customer's order details in its prose, and
     the id is its only bearer, so on a shared device it lives an hour rather than a day. Login-tied rows are
     protected server-side (a different login is refused). */
  const STORAGE_VERIFIED = 'ami_conversation_verified';
  const VERIFIED_MAX_AGE_MS = 60 * 60 * 1000;

  const OPEN_TIMEOUT_MS = 2000;
  const IDLE_RESOLUTION_MS = 90 * 1000;
  const DOCK_MIN_HEIGHT = 320;
  const DOCK_MAX_HEIGHT = 600;
  const DOCK_VIEWPORT_MARGIN = 12; // the rail's own page inset
  const RENDER_CHUNK = 24; // Discover's section-render chunk
  const GRID_COLUMNS = 2; // .ami-grid's track count; keep in step with ami.css
  const GRID_EAGER = 12; // tiles hydrated before "Show more" (a multiple of GRID_COLUMNS)
  const MAX_CLIENT_EVENTS = 20;
  const MESSAGE_MAX = 500;

  const routes = (window.themeVariables && window.themeVariables.routes) || {};
  const SEARCH_URL = routes.searchUrl || '/search';
  const CART_ADD_URL = `${routes.cartAddUrl || '/cart/add'}.js`;
  const CART_URL = `${routes.cartUrl || '/cart'}.js`;

  /* ---------- utilities ---------- */

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function storageGet(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      /* private mode: the conversation just does not survive a reload */
    }
  }

  function sessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  }

  /* The degrade choice is kept for ten minutes, not the whole tab: one transient 404 must not hold a customer
     on the context-less routes after the platform comes up. */
  const CORS_PIN_MS = 10 * 60 * 1000;
  function corsPinned() {
    const until = Number(sessionGet(STORAGE_TRANSPORT) || 0);
    return until > Date.now();
  }
  function pinCors() {
    sessionSet(STORAGE_TRANSPORT, String(Date.now() + CORS_PIN_MS));
  }

  /* Block prices travel in dollars (validators/amiStorefrontValidators.ts);
     custom.js's formatter takes cents and the shop's money format. */
  function money(dollars, currency) {
    const amount = Number(dollars);
    if (!Number.isFinite(amount)) return '';
    if (currency) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
      } catch {
        /* unknown code: fall through to the shop format */
      }
    }
    if (typeof window.formatShopMoney === 'function') {
      return window.formatShopMoney(Math.round(amount * 100));
    }
    return `$${amount.toFixed(2)}`;
  }

  /* Classification labels arrive as slugs ("social-media", "fonts"); shoppers see words. */
  function displayLabel(value) {
    const text = String(value || '').replace(/-/g, ' ');
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
  }

  function formatDate(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return String(iso);
    try {
      return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
    } catch {
      return date.toDateString();
    }
  }

  function posthogIds() {
    const ph = window.posthog;
    const ids = {};
    try {
      if (ph && typeof ph.get_distinct_id === 'function') ids.distinct = ph.get_distinct_id();
      if (ph && typeof ph.get_session_id === 'function') ids.session = ph.get_session_id();
    } catch {
      /* analytics never break the chat */
    }
    return ids;
  }

  function isDesignMode() {
    const rail = document.querySelector('bottom-rail');
    return Boolean(rail && rail.hasAttribute('data-ami-design-mode'));
  }

  function railAttr(name) {
    const rail = document.querySelector('bottom-rail');
    return rail ? rail.getAttribute(name) : null;
  }

  /* ---------- icons: Focal's stroke style at Focal's sizes ---------- */

  const ICONS = {
    close:
      '<svg viewBox="0 0 14 14" focusable="false" aria-hidden="true"><path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>',
    send:
      '<svg viewBox="0 0 12 10" focusable="false" aria-hidden="true" fill="none"><path d="M0 5L9.75 5M9.75 5L5.85 9.16M9.75 5L5.85 0.84" stroke="currentColor" stroke-width="1.5"/></svg>',
    check:
      '<svg viewBox="0 0 10 8" focusable="false" aria-hidden="true" fill="none"><path d="M1 4l2.7 2.7L9 1.3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    checkCircle:
      '<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.5 2.5 5-6"/></svg>',
    alert:
      '<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg>',
    thumbUp:
      '<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3Zm0 0 4.2-7.1a1.5 1.5 0 0 1 2.8.8V9h5.1a2 2 0 0 1 2 2.3l-1.2 7a2 2 0 0 1-2 1.7H7"/></svg>',
    thumbDown:
      '<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 14V3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-3Zm0 0-4.2 7.1a1.5 1.5 0 0 1-2.8-.8V15H4.9a2 2 0 0 1-2-2.3l1.2-7a2 2 0 0 1 2-1.7H17"/></svg>',
    // the help site's "Start new chat" glyph (lucide rotate-ccw), in the thumbs' stroke style
    newChat:
      '<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>',
    // snippets/icon.liquid 'header-download'
    file:
      '<svg viewBox="0 0 20 18" focusable="false" aria-hidden="true"><path d="M10 1V12M10 12L6 8M10 12L14 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M1 13V16C1 16.5523 1.44772 17 2 17H18C18.5523 17 19 16.5523 19 16V13" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    // snippets/wishlist-button.liquid's two hearts, outline and filled
    heartOutline:
      '<svg class="wishlist-btn__icon wishlist-btn__icon--outline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
    heartFilled:
      '<svg class="wishlist-btn__icon wishlist-btn__icon--filled" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
  };

  /* ---------- markdown: the small subset Ami writes ---------- */

  function inlineMarkdown(text) {
    let s = escapeHtml(text);
    s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/(^|[^*\w])\*([^*\n]+)\*(?!\w)/g, '$1<em>$2</em>');
    // Links: absolute http(s) or site-relative only; anything else stays text.
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g, (match, label, url) => {
      // `url` is already HTML-escaped (quotes are &quot;), so it cannot leave the attribute; the scheme check
      // is what keeps javascript: and protocol-relative targets out.
      if (!safeUrl(url)) return label;
      const rel = isExternalUrl(url) ? ' target="_blank" rel="noopener"' : '';
      return `<a href="${url}"${rel}>${label}</a>`;
    });
    return s;
  }

  /* Only http(s) and site-relative hrefs reach the page from server data or model text; anything else
     (javascript:, data:, protocol-relative) is dropped. */
  function safeUrl(value) {
    const url = String(value || '').trim();
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/') && !url.startsWith('//')) return url;
    return '';
  }

  function isExternalUrl(url) {
    try {
      return new URL(url, location.origin).origin !== location.origin;
    } catch {
      return true;
    }
  }

  /* Legacy text markers from the pre-frame protocol, kept out of sight. */
  function stripMarkers(text) {
    return String(text || '').replace(/\[(AUTH_REQUIRED|ESCALATE[^\]]*)\]/g, '').trim();
  }

  /* "- text", "* text", "• text" or "1. text" → the list kind and the item's text; null for anything else. */
  function listItem(line) {
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (bullet) return { kind: 'ul', text: bullet[1] };
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    if (numbered) return { kind: 'ol', text: numbered[1] };
    return null;
  }

  function renderMarkdown(source) {
    const lines = stripMarkers(source).replace(/\r/g, '').split('\n');
    let html = '';
    let list = null;
    let para = [];
    const flushPara = () => {
      if (para.length === 0) return;
      html += `<p>${inlineMarkdown(para.join(' '))}</p>`;
      para = [];
    };
    const flushList = () => {
      if (!list) return;
      html += `</${list}>`;
      list = null;
    };
    for (const raw of lines) {
      const line = raw.trim();
      if (!line) {
        flushPara();
        flushList();
        continue;
      }
      const heading = line.match(/^#{1,6}\s+(.*)$/);
      if (heading) {
        flushPara();
        flushList();
        html += `<p class="ami-md__h">${inlineMarkdown(heading[1])}</p>`;
        continue;
      }
      const item = listItem(line);
      if (item) {
        flushPara();
        if (list !== item.kind) {
          flushList();
          list = item.kind;
          html += `<${item.kind}>`;
        }
        html += `<li>${inlineMarkdown(item.text)}</li>`;
        continue;
      }
      flushList();
      para.push(line);
    }
    flushPara();
    flushList();
    return html;
  }

  /* The theme's own controls, so what the chat draws is what the product page draws:
     - the wishlist heart is snippets/wishlist-button.liquid's markup (its delegated click handler on
       document toggles window.Wishlist and syncs every [data-wishlist-button] on wishlist:updated);
     - Focal's spinner is Discover's .dsc-spinner;
     - buttons are .button.button--primary / .button--outline (theme.css). */
  function wishlistHeart(product) {
    return `<button type="button" class="wishlist-btn ami-heart" data-wishlist-button data-product-id="${escapeHtml(product.shopify_id)}" data-product-handle="${escapeHtml(product.handle)}" aria-label="Add to wishlist">${ICONS.heartOutline}${ICONS.heartFilled}</button>`;
  }

  const SPINNER = '<span class="dsc-spinner" aria-hidden="true"></span>';

  /* The wait before Ami's first word, filled the way The Sims fills a loading screen: design words doing
     things they cannot do. Visual only; the aria-live line reads the finished answer, never these. */
  const THINKING_PHRASES = [
    'Reticulating swatches…',
    'Kerning the clouds…',
    'Ironing the grid…',
    'Watering the layers…',
    'Teaching a Bézier to sit…',
    'Feeding the pixels…',
    'Untangling the golden ratio…',
    'Sanding down a gradient…',
    'Herding the anchor points…',
    'Polishing the negative space…',
    'Whispering to the baseline…',
    'Convincing the mockup…',
    'Defrosting a texture…',
    'Counting the clipping masks, twice…',
    'Renaming Final_v2…',
  ];
  const THINKING_LATE = 'Nearly there…';
/* The empty state's opener, after "Hi," or "Hi <name>,". One line everywhere: the page's suggestion chips under it
   carry the page (snippets/ami-suggestions.liquid), so the opener never has to. */
const STARTER_OPENER =
  "I'm Ami, Studio 2am's AI assistant. Ask me anything, from mockups and textures to licences and downloads."; // a real signal, after THINKING_LATE_MS; it then stays
  const THINKING_ROTATE_MS = 2500;
  const THINKING_LATE_MS = 10 * 1000;

  function pickThinking(except) {
    const pool = THINKING_PHRASES.filter((phrase) => phrase !== except);
    return pool[Math.floor(Math.random() * pool.length)];
  }

  /* ---------- section-rendered product cards, as Discover does it ---------- */

  const Cards = {
    cache: new Map(), // shopify_id -> product-item node (cloned on use) | null
    pending: new Map(), // shopify_id -> in-flight fetch, so a grid and a card in one turn fetch each id once

    async fetchNodes(items) {
      const chunks = [];
      for (let i = 0; i < items.length; i += RENDER_CHUNK) chunks.push(items.slice(i, i + RENDER_CHUNK));
      let complete = true;
      const htmlChunks = await Promise.all(
        chunks.map((chunk) => {
          const query = chunk.map((item) => `id:${item.shopify_id}`).join(' OR ');
          const url = `${SEARCH_URL}?q=${encodeURIComponent(query)}&type=product&section_id=discover-results-grid`;
          return fetch(url)
            .then((response) => (response.ok ? response.text() : ((complete = false), '')))
            .catch(() => ((complete = false), ''));
        })
      );
      const byId = new Map();
      htmlChunks.forEach((html) => {
        if (!html) return;
        const holder = document.createElement('div');
        holder.innerHTML = html;
        Cards.liftWishlistAssets(holder);
        holder.querySelectorAll('.product-item[data-product-id]').forEach((node) => {
          byId.set(String(node.getAttribute('data-product-id')), node);
        });
      });
      return { byId, complete };
    },

    /* snippets/wishlist-button.liquid carries its styles and its click handler inline, so they only reach a
       page that renders a product grid (a blog post has none). The section render includes both; they are
       lifted into the head the first time so the hearts here behave exactly as they do on a collection page.
       The script guards itself with window.__wishlistButtonsInit, so lifting it twice is harmless. */
    liftWishlistAssets(holder) {
      if (!document.getElementById('ami-wishlist-style')) {
        const style = Array.from(holder.querySelectorAll('style')).find((node) => node.textContent.includes('.wishlist-btn'));
        if (style) {
          style.id = 'ami-wishlist-style';
          document.head.appendChild(style);
        }
      }
      if (!window.__wishlistButtonsInit) {
        const source = Array.from(holder.querySelectorAll('script')).find((node) => node.textContent.includes('__wishlistButtonsInit'));
        if (source) {
          const script = document.createElement('script');
          script.textContent = source.textContent;
          document.head.appendChild(script);
        }
      }
    },

    /** Card nodes for the given rows, in the rows' order; null where Shopify has no card. */
    async nodesFor(rows) {
      const missing = rows.filter((row) => {
        const id = String(row.shopify_id);
        return !this.cache.has(id) && !this.pending.has(id);
      });
      if (missing.length > 0) {
        const request = this.fetchNodes(missing)
          .then(({ byId, complete }) => {
            missing.forEach((row) => {
              const id = String(row.shopify_id);
              const node = byId.get(id) || null;
              // A failed fetch leaves the id uncached so a later render retries.
              if (node || complete) this.cache.set(id, node);
            });
          })
          .finally(() => missing.forEach((row) => this.pending.delete(String(row.shopify_id))));
        missing.forEach((row) => this.pending.set(String(row.shopify_id), request));
      }
      await Promise.all(rows.map((row) => this.pending.get(String(row.shopify_id))).filter(Boolean));
      return rows.map((row) => {
        const node = this.cache.get(String(row.shopify_id));
        return node ? node.cloneNode(true) : null;
      });
    },

    /** Primary image of a card, for thumbnails in rows. */
    imageOf(node) {
      const img = node && node.querySelector('img.product-item__primary-image');
      if (!img) return null;
      return { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
    },
  };

  /* snippets/wishlist-button.liquid syncs hearts on this event (gotchas), and the header's badge listener reads
     `detail.count` from it, so the detail has to carry what assets/wishlist.js sends. */
  function syncWishlistHearts() {
    const wishlist = window.Wishlist;
    const items = wishlist ? wishlist.getItems() : [];
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items, count: items.length } }));
  }

  /* ---------- SSE reader ---------- */

  async function readFrames(response, onFrame) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let index;
      while ((index = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, index).trim();
        buffer = buffer.slice(index + 1);
        if (!line.startsWith('data:')) continue;
        try {
          onFrame(JSON.parse(line.slice(5).trim()));
        } catch {
          /* a partial or malformed line: skip it */
        }
      }
    }
  }

  /* ---------- tool pill labels ---------- */

  const TOOL_LABELS = {
    ami_get_orders: 'Looking up your orders',
    ami_get_download_links: 'Getting your download links',
    ami_get_license_info: 'Checking your licences',
    ami_get_account_details: 'Checking your account',
    ami_search_help_center: 'Searching the help centre',
    search_products: 'Searching the catalogue',
    similar_products: 'Finding similar products',
    get_product_details: 'Reading the product details',
    compare_products: 'Comparing products',
    build_bundle: 'Building a bundle',
    get_license_guidance: 'Reading the licence',
    create_support_thread: 'Creating a support thread',
  };
  // Bookkeeping tools the customer should not see as work.
  const SILENT_TOOLS = new Set(['report_unknown_fact', 'request_verification']);

  /* ---------- block renderers ----------
     Every block is a `.ami-block[data-ami-block=<kind>]`. Product identity is
     {shopify_id, handle}; visuals come from the section renderer (Cards). */

  const Blocks = {
    /* Set by the element: what to do when Focal's product form reports a successful add. */
    onVariantAdded() {},

    productLink(row) {
      return `/products/${encodeURIComponent(row.handle)}`;
    },

    fallbackRow(row) {
      const by = row.creator_name ? `by ${escapeHtml(row.creator_name)}` : '';
      const price = row.is_free ? '<span class="ami-price--free">Free</span>' : '';
      return `<a class="ami-row" href="${this.productLink(row)}" data-ami-target="product"><span class="ami-row__body"><span class="ami-row__title">${escapeHtml(row.title)}</span><span class="ami-row__meta">${by}</span></span><span class="ami-row__end">${price}</span></a>`;
    },

    /** Fill a grid container with section-rendered cards, in order. */
    async hydrate(container, rows) {
      const nodes = await Cards.nodesFor(rows);
      const holder = document.createDocumentFragment();
      rows.forEach((row, index) => {
        const node = nodes[index];
        if (node) {
          node.setAttribute('data-ami-target', 'product');
          holder.appendChild(node);
        } else {
          const wrap = document.createElement('div');
          wrap.innerHTML = this.fallbackRow(row);
          holder.appendChild(wrap.firstElementChild);
        }
      });
      container.innerHTML = '';
      container.appendChild(holder);
      syncWishlistHearts();
    },

    skeletons(count) {
      return Array.from({ length: count }, () => '<div class="ami-skeleton" aria-hidden="true"></div>').join('');
    },

    product_grid(data) {
      const el = document.createElement('div');
      el.className = 'ami-block';
      const total = typeof data.total_matches === 'number' ? data.total_matches : data.products.length;
      const seeAll = safeUrl(data.discover_url)
        ? `<span class="ami-block__meta"><a href="${escapeHtml(safeUrl(data.discover_url))}" data-ami-target="search">See all ${total > data.products.length ? total : ''} in search</a></span>`
        : '';
      /* Two columns, full rows only: an orphan tile in the last row is what "See all in search" is for. A
         single result still shows on its own. */
      const rows = data.products.length < GRID_COLUMNS ? data.products : data.products.slice(0, Math.floor(data.products.length / GRID_COLUMNS) * GRID_COLUMNS);
      const eager = rows.slice(0, GRID_EAGER);
      const rest = rows.slice(GRID_EAGER);
      el.innerHTML =
        `<div class="ami-block__head"><span class="ami-block__title">${data.query ? `Results for “${escapeHtml(data.query)}”` : 'Products'}</span>${seeAll}</div>` +
        `<div class="ami-grid" data-ami-grid>${this.skeletons(eager.length)}</div>` +
        (rest.length > 0
          ? `<button type="button" class="button button--outline button--small ami-grid__more" data-ami-more>Show ${rest.length} more</button>`
          : '');
      const grid = el.querySelector('[data-ami-grid]');
      this.hydrate(grid, eager);
      const more = el.querySelector('[data-ami-more]');
      if (more) {
        more.addEventListener(
          'click',
          async () => {
            more.hidden = true;
            const extra = document.createElement('div');
            extra.className = 'ami-grid';
            extra.innerHTML = this.skeletons(rest.length);
            grid.after(extra);
            await this.hydrate(extra, rest);
          },
          { once: true }
        );
      }
      return el;
    },

    /** The font rows of the fact list: styles, languages, glyphs, features, axes, single-style pricing. */
    fontFacts(font, list, push) {
      const names = list(font.style_names, 6);
      push('Styles', `${font.style_count}${names ? ` · ${names}` : ''}`);
      if (font.languages.length > 0 || font.partial_languages.length > 0) {
        const partial = font.partial_languages.length > 0 ? ` (${font.partial_languages.length} partial)` : '';
        push('Languages', `${font.languages.length}${partial}${font.scripts.length ? ` · ${list(font.scripts, 4)}` : ''}`);
      }
      if (font.glyph_count_max !== null) {
        const range =
          font.glyph_count_min !== null && font.glyph_count_min !== font.glyph_count_max
            ? `${font.glyph_count_min}–${font.glyph_count_max}`
            : String(font.glyph_count_max);
        push('Glyphs', escapeHtml(range));
      }
      if (font.opentype_features.length > 0) push('OpenType', `${font.opentype_features.length} features · ${list(font.opentype_features, 6)}`);
      if (font.is_variable && font.axes.length > 0) {
        push('Variable', font.axes.map((axis) => `${escapeHtml(axis.name || axis.tag)} ${axis.min}–${axis.max}`).join(', '));
      }
      if (font.individual_styles && font.individual_styles.price !== null) {
        push('Single styles', `from ${escapeHtml(money(font.individual_styles.price))}`);
      }
    },

    factList(facts, product) {
      const rows = [];
      const list = (values, cap) => {
        if (!Array.isArray(values) || values.length === 0) return null;
        const shown = values.slice(0, cap);
        const more = values.length > cap ? ` +${values.length - cap} more` : '';
        return `${shown.map(escapeHtml).join(', ')}${more}`;
      };
      const push = (label, value) => {
        if (value === null || value === undefined || value === '') return;
        rows.push(`<dt>${label}</dt><dd>${value}</dd>`);
      };
      if (facts.price_from !== null && !product.is_free) push('From', escapeHtml(money(facts.price_from)));
      push('Formats', list(facts.file_formats, 6));
      push('Key features', list(facts.key_features, 4));
      if (facts.font) this.fontFacts(facts.font, list, push);
      push('Works with', list(facts.compatibility, 6));
      if (facts.files) {
        const folders = facts.files.top_level_folders.length ? ` · ${list(facts.files.top_level_folders, 4)}` : '';
        push('Files', `${facts.files.total_files} in ${facts.files.downloads} download${facts.files.downloads === 1 ? '' : 's'}${folders}`);
      }
      push('Size', facts.file_size ? escapeHtml(facts.file_size) : null);
      push('Dimensions', facts.dimensions ? escapeHtml(facts.dimensions) : null);
      push('Resolution', facts.dpi !== null ? `${facts.dpi} dpi` : null);
      if (facts.layered !== null) push('Layered', facts.layered ? 'Yes' : 'No');
      if (facts.rating) push('Rating', `${facts.rating.value} (${facts.rating.count})`);
      const unknown = Array.isArray(facts.unknown) && facts.unknown.length > 0 ? facts.unknown : null;
      if (unknown) rows.push(`<dt>Not listed</dt><dd class="is-unknown">${unknown.map((k) => escapeHtml(k.replace(/_/g, ' '))).join(', ')}</dd>`);
      return rows.length ? `<dl class="ami-facts">${rows.join('')}</dl>` : '';
    },

    variantRow(variant, selected) {
      const compare =
        variant.compare_at_price !== null && variant.compare_at_price > variant.price
          ? `<span class="price price--compare">${escapeHtml(money(variant.compare_at_price))}</span>`
          : '';
      return `<label class="ami-variant${selected ? ' is-selected' : ''}"><input type="radio" name="ami-variant" value="${escapeHtml(variant.id)}"${selected ? ' checked' : ''}><span class="ami-variant__check">${ICONS.check}</span><span class="ami-variant__name">${escapeHtml(variant.title)}</span><span class="ami-variant__price">${compare}<span class="price${compare ? ' price--highlight' : ''}">${escapeHtml(money(variant.price))}</span></span></label>`;
    },

    product_card(data) {
      const el = document.createElement('div');
      el.className = 'ami-block';
      const product = data.product;
      const link = this.productLink(product);
      const by = [product.creator_name ? `by ${escapeHtml(product.creator_name)}` : '', product.category ? escapeHtml(displayLabel(product.category)) : '']
        .filter(Boolean)
        .join(' · ');
      const variants = product.is_free ? [] : data.variants;
      const single = variants.length === 1;
      /* The buy is Focal's own product form (theme.js ProductForm, is="product-form"): it posts the hidden
         variant id, fires cart:updated / cart:refresh, opens the mini cart and shows the cart notification,
         exactly as the product page does. The licence rows above write into the hidden id. */
      const buy = product.is_free
        ? `<a class="button button--primary button--full" href="${link}" data-ami-target="product">Free download</a>`
        : variants.length > 0
          ? `<form is="product-form" class="ami-buy" method="post" action="${escapeHtml(routes.cartAddUrl || '/cart/add')}" data-ami-buy><input type="hidden" name="id" value="${single ? escapeHtml(variants[0].id) : ''}"><input type="hidden" name="quantity" value="1"><button type="submit" is="loader-button" class="button button--primary button--full" data-ami-add data-button-content="Add to cart"${single ? '' : ' disabled'}>Add to cart</button></form>`
          : '';
      el.innerHTML =
        `<div class="ami-card">` +
        `<div class="ami-card__media" data-ami-media></div>` +
        `<div class="ami-card__body">` +
        `<div><a class="ami-card__title" href="${link}" data-ami-target="product">${escapeHtml(product.title)}</a>${by ? `<div class="ami-card__by">${by}</div>` : ''}</div>` +
        (data.facts.overview ? `<p class="ami-card__overview">${escapeHtml(data.facts.overview)}</p>` : '') +
        this.factList(data.facts, product) +
        (variants.length > 0 ? `<div class="ami-variants" data-ami-variants>${variants.map((v, i) => this.variantRow(v, single && i === 0)).join('')}</div>` : '') +
        `<div class="ami-actions">${buy}${wishlistHeart(product)}</div>` +
        `<div><a class="dsc-chip" href="${link}" data-ami-target="product">View product</a></div>` +
        `<p class="ami-status" data-ami-status hidden></p>` +
        `</div></div>`;
      const form = el.querySelector('[data-ami-buy]');
      if (form) form.addEventListener('variant:added', () => Blocks.onVariantAdded(form, product));
      const media = el.querySelector('[data-ami-media]');
      Cards.nodesFor([product]).then(([node]) => {
        if (node) media.appendChild(node);
        else media.remove();
        syncWishlistHearts();
      });
      return el;
    },

    compareCell(product, attribute) {
      if (!Object.prototype.hasOwnProperty.call(product.values, attribute.key)) return '<td aria-label="Not applicable"></td>';
      const value = product.values[attribute.key];
      if (value === null) return '<td class="is-unknown" title="Not listed">—</td>';
      if (typeof value === 'boolean') return `<td>${value ? 'Yes' : 'No'}</td>`;
      return `<td>${escapeHtml(value)}</td>`;
    },

    comparison(data) {
      const el = document.createElement('div');
      el.className = 'ami-block';
      const head = data.products
        .map(
          (product) =>
            `<th><a href="${this.productLink(product)}" data-ami-target="product">${escapeHtml(product.title)}</a>${product.creator_name ? `<small>${escapeHtml(product.creator_name)}</small>` : ''}</th>`
        )
        .join('');
      const body = data.attributes
        .map(
          (attribute) =>
            `<tr><td>${escapeHtml(attribute.label)}</td>${data.products.map((product) => this.compareCell(product, attribute)).join('')}</tr>`
        )
        .join('');
      el.innerHTML = `<div class="ami-compare"><table><thead><tr><th></th>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
      return el;
    },

    orders(data) {
      const el = document.createElement('div');
      el.className = 'ami-block';
      const rows = data.orders
        .map((order) => {
          const items = order.items
            .map((item) => {
              const title = item.handle
                ? `<a href="/products/${encodeURIComponent(item.handle)}" data-ami-target="product">${escapeHtml(item.title)}</a>`
                : escapeHtml(item.title);
              return `<li><span>${title}</span>${item.license ? `<span class="ami-row__meta">${escapeHtml(item.license)}</span>` : ''}</li>`;
            })
            .join('');
          return `<div class="ami-row"><div class="ami-row__body"><span class="ami-row__title">Order ${escapeHtml(order.order_number)}</span><span class="ami-row__meta">${escapeHtml(formatDate(order.date))}</span><ul class="ami-row__items">${items}</ul></div><div class="ami-row__end">${escapeHtml(money(order.total, order.currency))}</div></div>`;
        })
        .join('');
      el.innerHTML = `<div class="ami-block__head"><span class="ami-block__title">Your orders</span></div><div class="ami-rows">${rows || '<p class="ami-status">No orders on this account yet.</p>'}</div>`;
      return el;
    },

    downloads(data) {
      const el = document.createElement('div');
      el.className = 'ami-block';
      const rows = data.downloads
        .map((download) => {
          const files = download.files
            .map(
              (file) =>
                `<li>${safeUrl(file.url) ? `<a class="ami-file" href="${escapeHtml(safeUrl(file.url))}" data-ami-target="file">${ICONS.file}${escapeHtml(file.name)}</a>` : `<span>${escapeHtml(file.name)}</span>`}${file.expires_at ? `<span class="ami-row__meta">Link expires ${escapeHtml(formatDate(file.expires_at))}</span>` : ''}</li>`
            )
            .join('');
          const title = download.handle
            ? `<a class="ami-row__title" href="/products/${encodeURIComponent(download.handle)}" data-ami-target="product">${escapeHtml(download.title)}</a>`
            : `<span class="ami-row__title">${escapeHtml(download.title)}</span>`;
          return `<div class="ami-row"><div class="ami-row__body">${title}<ul class="ami-row__items">${files}</ul></div></div>`;
        })
        .join('');
      el.innerHTML = `<div class="ami-block__head"><span class="ami-block__title">Your downloads</span></div><div class="ami-rows">${rows || '<p class="ami-status">No downloads found.</p>'}</div>`;
      return el;
    },

    licenses(data) {
      const el = document.createElement('div');
      el.className = 'ami-block';
      const rows = data.licenses
        .map((license) => {
          const meta = [license.license_title + (license.version ? ` ${license.version}` : ''), license.purchased_at ? formatDate(license.purchased_at) : '']
            .filter(Boolean)
            .join(' · ');
          const doc = safeUrl(license.document_url)
            ? `<a class="ami-file" href="${escapeHtml(safeUrl(license.document_url))}" target="_blank" rel="noopener" data-ami-target="document">View</a>`
            : '';
          return `<div class="ami-row"><div class="ami-row__body"><span class="ami-row__title">${escapeHtml(license.product)}</span><span class="ami-row__meta">${escapeHtml(meta)}</span></div><div class="ami-row__end">${doc}</div></div>`;
        })
        .join('');
      el.innerHTML = `<div class="ami-block__head"><span class="ami-block__title">Your licences</span></div><div class="ami-rows">${rows || '<p class="ami-status">No licences found.</p>'}</div>`;
      return el;
    },

    ladderLine(ladder, count) {
      const rungs = (ladder || []).filter((rung) => rung.threshold <= count).sort((a, b) => b.threshold - a.threshold);
      const current = rungs[0] || null;
      const next = (ladder || []).filter((rung) => rung.threshold > count).sort((a, b) => a.threshold - b.threshold)[0] || null;
      const parts = [];
      if (current && current.percent > 0) parts.push(`${current.percent}% off for ${current.threshold}+ items`);
      if (next) parts.push(`add ${next.threshold - count} more for ${next.percent}% off`);
      return parts.length ? `<div class="ami-bundle__ladder">${escapeHtml(parts.join(' · '))}</div>` : '';
    },

    bundle(data) {
      const el = document.createElement('div');
      el.className = 'ami-block';
      const rows = data.items
        .map(
          (item) =>
            `<a class="dsc-hit" href="${this.productLink(item)}" data-ami-target="product"><span class="dsc-hit__thumb" data-ami-thumb-for="${escapeHtml(item.shopify_id)}"></span><span class="dsc-hit__body"><span class="dsc-hit__title">${escapeHtml(item.title)}</span><span class="dsc-hit__meta">${item.creator_name ? `by ${escapeHtml(item.creator_name)}` : ''}</span></span><span class="dsc-hit__price">${escapeHtml(money(item.price))}</span></a>`
        )
        .join('');
      const discounted = data.discounted_total < data.subtotal;
      const total = discounted
        ? `<s>${escapeHtml(money(data.subtotal))}</s>${escapeHtml(money(data.discounted_total))}`
        : escapeHtml(money(data.subtotal));
      const ids = data.items.map((item) => item.variant_id);
      el.innerHTML =
        `<div class="ami-block__head"><span class="ami-block__title">Bundle · ${data.items.length} item${data.items.length === 1 ? '' : 's'}</span></div>` +
        `<div class="ami-rows">${rows}</div>` +
        `<div class="ami-bundle__sum"><span>Total</span><strong>${total}</strong></div>` +
        this.ladderLine(data.ladder, data.items.length) +
        `<div class="ami-block__foot"><div class="ami-actions"><button type="button" class="button button--primary button--full" data-ami-add-all data-ami-variant="${escapeHtml(ids.join(','))}">Add all to cart</button></div><p class="ami-status" data-ami-status hidden></p></div>`;
      Cards.nodesFor(data.items).then((nodes) => {
        nodes.forEach((node, index) => {
          const image = Cards.imageOf(node);
          const thumb = el.querySelector(`[data-ami-thumb-for="${data.items[index].shopify_id}"]`);
          if (!image || !thumb) return;
          const img = document.createElement('img');
          img.alt = '';
          img.loading = 'lazy';
          if (image.src) img.src = image.src;
          if (image.srcset) img.srcset = image.srcset;
          img.sizes = '48px';
          img.addEventListener('load', () => thumb.classList.add('is-loaded'), { once: true });
          thumb.appendChild(img);
        });
      });
      return el;
    },

    action_result(data) {
      const el = document.createElement('p');
      el.className = 'ami-status';
      el.textContent = data.detail || (data.ok ? 'Done.' : 'That did not work.');
      return el;
    },

    /** Account data is shown once and never stored: a restore draws this instead. */
    redacted(kind) {
      const label = { orders: 'orders', downloads: 'download links', licenses: 'licences' }[kind] || 'details';
      const ask = { orders: 'Show my orders', downloads: 'Show my downloads', licenses: 'Show my licences' }[kind] || 'Show that again';
      const el = document.createElement('div');
      el.className = 'ami-block ami-block--redacted';
      el.innerHTML = `<span>Your ${label} were shown here. Ask again to see them.</span><button type="button" class="dsc-chip" data-ami-ask="${escapeHtml(ask)}">${escapeHtml(ask)}</button>`;
      return el;
    },

    render(block) {
      const renderer = Object.prototype.hasOwnProperty.call(this, block.kind) ? this[block.kind] : null;
      if (block.redacted || !block.data) return this.redacted(block.kind);
      if (typeof renderer !== 'function') return null;
      try {
        const el = renderer.call(this, block.data);
        if (el) {
          el.setAttribute('data-ami-block', block.kind);
          el.setAttribute('data-ami-block-id', block.id);
        }
        return el;
      } catch {
        return null;
      }
    },
  };

  /* ---------- the element ---------- */

  class AmiChat extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.mode = null;
      /* Docked, the card is content-sized while the log holds only the starters (compact) and grows to its full
         height on the first send; once grown it stays tall for the session, a new chat included. */
      this.compact = false;
      this.grown = false;
      this.conversationId = null;
      this.verified = false;
      this.firstName = null;
      this.signedIn = false;
      this.turnIndex = 0;
      this.answers = 0;
      this.busy = false;
      this.stick = true;
      this.pendingEvents = [];
      this.surface = 'rail';
      this.openedAt = 0;
      this.resolutionShown = false;
      this.idleTimer = 0;
      this.thinkingTimer = 0;
      this.lastQuestion = '';
      this.restored = false;
      this.rafPosition = 0;
    }

    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      /* Never name an element reference `title`, `hidden`, `id` or `dir`: those shadow HTMLElement properties
         (the <discover-hero> bug, assets/discover.js). */
      this.innerHTML =
        `<div class="ami-chat__head"><span class="ami-chat__title">Ami</span><span class="ami-chat__who" data-ami-who></span><button type="button" class="ami-chat__new" data-ami-new-chat aria-label="Start a new chat" hidden>${ICONS.newChat}</button><button type="button" class="ami-chat__close" data-ami-close aria-label="Close chat">${ICONS.close}</button></div>` +
        `<div class="ami-chat__log" data-ami-log aria-label="Conversation with Ami"></div>` +
        '<div class="visually-hidden" data-ami-announce aria-live="polite"></div>' +
        `<form class="ami-chat__composer" data-ami-composer><span class="ami-chat__mark" aria-hidden="true">✦</span><label class="visually-hidden" for="ami-chat-input">Ask Ami anything</label><input id="ami-chat-input" class="ami-chat__input" type="text" autocomplete="off" maxlength="${MESSAGE_MAX}" placeholder="Ask Ami anything" data-ami-input><input class="ami-chat__hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" data-ami-hp><button type="submit" class="ami-chat__send" data-ami-send aria-label="Send to Ami">${ICONS.send}</button></form>`;
      this.logEl = this.querySelector('[data-ami-log]');
      this.inputEl = this.querySelector('[data-ami-input]');
      this.sendEl = this.querySelector('[data-ami-send]');
      this.whoEl = this.querySelector('[data-ami-who]');
      this.newEl = this.querySelector('[data-ami-new-chat]');
      this.announceEl = this.querySelector('[data-ami-announce]');
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-label', 'Chat with Ami');

      Blocks.onVariantAdded = (form, product) => this.onVariantAdded(form, product);
      this.querySelector('[data-ami-composer]').addEventListener('submit', (event) => {
        event.preventDefault();
        const text = this.inputEl.value.trim();
        if (!text) return;
        this.inputEl.value = '';
        this.send(text);
      });
      this.querySelector('[data-ami-close]').addEventListener('click', () => this.close({ fromUser: true }));
      this.newEl.addEventListener('click', () => this.requestNewChat('header'));
      this.logEl.addEventListener('click', (event) => this.onLogClick(event));
      this.logEl.addEventListener('scroll', () => {
        const { scrollTop, scrollHeight, clientHeight } = this.logEl;
        this.stick = scrollHeight - scrollTop - clientHeight < 48;
      });
      this._onKeydown = (event) => {
        if (!this.isOpen) return;
        // Escape dismisses the dialog outright; the resolution line only intercepts the close button.
        if (event.key === 'Escape') this.close({ fromUser: false });
        if (event.key === 'Tab' && this.mode === 'full') this.trapTab(event);
      };
      document.addEventListener('keydown', this._onKeydown);
      this._onReposition = () => this.schedulePosition();
      // The rail is fixed, so only a resize (which includes a mobile address bar) can move the anchor.
      window.addEventListener('resize', this._onReposition, { passive: true });
    }

    /* Full screen is a modal: Tab cycles inside the card rather than into the page behind it. */
    trapTab(event) {
      const focusable = Array.from(
        this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((node) => node.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
      window.removeEventListener('resize', this._onReposition);
      window.clearTimeout(this.idleTimer);
      this.stopThinking();
      // Removed while open (editor re-render): never leave the page locked
      document.documentElement.classList.remove(OPEN_CLASS);
      this.setPageLock(false);
    }

    /* ---- analytics ---- */

    track(event, props) {
      try {
        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture(event, { surface: this.surface, conversation_id: this.conversationId, ...(props || {}) });
        }
      } catch {
        /* analytics never break the chat */
      }
    }

    /* ---- geometry ---- */

    anchor() {
      return document.querySelector(ANCHOR_SELECTOR);
    }

    schedulePosition() {
      if (!this.isOpen || this.rafPosition) return;
      this.rafPosition = window.requestAnimationFrame(() => {
        this.rafPosition = 0;
        this.position();
      });
    }

    position() {
      const anchor = this.anchor();
      const rect = anchor ? anchor.getBoundingClientRect() : null;
      const available = rect ? rect.bottom - DOCK_VIEWPORT_MARGIN : 0;
      /* A zero-sized rect means the rail has hidden the capsule (it does below 1000px once the open class is
         on), which is the signal to take the screen: the theme's breakpoint decides, not a second one here. */
      const canDock = Boolean(rect && rect.width > 0 && rect.height > 0 && available >= DOCK_MIN_HEIGHT && !COMPACT.matches);
      const mode = canDock ? 'docked' : 'full';
      if (mode !== this.mode) {
        this.mode = mode;
        this.classList.toggle('ami-chat--docked', canDock);
        this.classList.toggle('ami-chat--full', !canDock);
        this.setAttribute('aria-modal', canDock ? 'false' : 'true');
        this.setPageLock(!canDock);
      }
      if (!canDock) {
        this.style.cssText = '';
        return;
      }
      const full = Math.min(DOCK_MAX_HEIGHT, available);
      this.style.left = `${Math.round(rect.left)}px`;
      this.style.bottom = `${Math.round(window.innerHeight - rect.bottom)}px`;
      this.style.width = `${Math.round(rect.width)}px`;
      let height = full;
      if (this.compact) {
        /* Content-sized: measure with the height unset, then pin the pixels, so the grow to the full height on
           the first send is a real transition (auto to px does not animate). */
        this.style.height = 'auto';
        height = Math.min(full, this.offsetHeight);
      }
      this.style.height = `${Math.round(height)}px`;
      this.style.top = 'auto';
      this.style.right = 'auto';
    }

    /* The ring's surface, line and gradient are declared by the theme on the capsule (its own computed border
       is transparent under the ring, and its background is two gradients), so they are read off it, once per
       open: they only change with a theme reload. */
    readCapsuleLook() {
      const anchor = this.anchor();
      if (!anchor) return;
      const anchorStyle = window.getComputedStyle(anchor);
      const pairs = [
        ['--s2-ami-card-surface', '--s2-ami-surface'],
        ['--s2-ami-card-border', '--s2-ami-line'],
        ['--s2-ami-card-ring', '--s2-ami-ring'],
      ];
      pairs.forEach(([from, to]) => {
        const value = anchorStyle.getPropertyValue(from).trim();
        if (value) this.style.setProperty(to, value);
      });
    }

    /* Focal's scroll lock is the `lock-all` class on the root (gotchas). Only a lock this element added is
       released, so a Focal drawer open at the same time keeps its own. */
    setPageLock(locked) {
      const root = document.documentElement.classList;
      if (locked && !root.contains('lock-all')) {
        root.add('lock-all');
        this.lockedPage = true;
      } else if (!locked && this.lockedPage) {
        root.remove('lock-all');
        this.lockedPage = false;
      }
    }

    /* ---- open / close ---- */

    async open(question, options) {
      const opts = options || {};
      this.surface = opts.surface || 'rail';
      const hasQuestion = typeof question === 'string' && question.trim().length > 0;
      if (!this.isOpen) {
        this.isOpen = true;
        this.openedAt = Date.now();
        this.turnsAtOpen = this.turnIndex;
        if (!this.conversationId) this.conversationId = this.storedConversationId();
        /* The class goes on FIRST: the rail reacts to it (fades the capsule, hides the rail below 1000px), and
           that last part changes the anchor's rect to nothing, which is what position() reads. */
        document.documentElement.classList.add(OPEN_CLASS);
        this.mode = null;
        this.readCapsuleLook();
        /* No conversation to restore: the starters go in BEFORE the first position() so the compact card is
           measured with them and opens at its final size, rather than growing during the fade. */
        if (!this.grown && !this.conversationId && !isDesignMode()) this.drawStarters();
        this.hidden = false; // created hidden so an unstyled element never shows at the page's foot (CSS keeps it invisible until [open])
        this.position();
        this.clientWidth; // flush layout so the open transition runs
        this.setAttribute('open', '');
        this.track('Ami Opened', { has_question: hasQuestion, ...(opts.track || {}) });
        if (isDesignMode()) {
          this.note('Ami is switched off inside the theme editor.');
        } else if (!this.restored) {
          this.restored = true;
          await this.restore();
        }
        if (this.turnIndex === 0 && this.answers === 0 && !isDesignMode() && !this.logEl.querySelector('[data-ami-starters]')) {
          this.drawStarters();
        }
      }
      // Focus moves into the card either way: below 1000px the rail (and the button that was focused)
      // disappears while the chat is open, and focus must not fall to the page behind the modal.
      this.focusInput();
      if (hasQuestion && !isDesignMode()) this.send(question.trim().slice(0, MESSAGE_MAX));
    }

    focusInput() {
      try {
        this.inputEl.focus({ preventScroll: true });
      } catch {
        /* ignore */
      }
    }

    close(options) {
      if (!this.isOpen) return;
      const fromUser = Boolean(options && options.fromUser);
      this.dropNewChatConfirm();
      /* The resolution line appears once per conversation, on the way out if idle never asked it (plan §4.8).
         The first close shows it and keeps the card open; the next close goes through. */
      if (fromUser && this.answers > 0 && !this.resolutionShown && !this.resolutionAsked()) {
        this.showResolution();
        return;
      }
      this.isOpen = false;
      this.removeAttribute('open');
      document.documentElement.classList.remove(OPEN_CLASS);
      this.setPageLock(false);
      window.clearTimeout(this.idleTimer);
      this.track('Ami Closed', {
        turns: this.turnIndex - (this.turnsAtOpen || 0),
        duration_s: Math.round((Date.now() - this.openedAt) / 1000),
      });
      const anchor = this.anchor();
      const back = anchor && anchor.querySelector(COMPACT.matches ? '[data-rail-ami-send]' : '[data-rail-ami-input]');
      if (back) {
        try {
          back.focus({ preventScroll: true });
        } catch {
          /* ignore */
        }
      }
    }

    /* ---- conversation bookkeeping ---- */

    storedConversationId() {
      const id = storageGet(STORAGE_ID);
      const at = Number(storageGet(STORAGE_AT) || 0);
      if (!id) return null;
      const maxAge = storageGet(STORAGE_VERIFIED) === id ? VERIFIED_MAX_AGE_MS : CONVERSATION_MAX_AGE_MS;
      if (!at || Date.now() - at > maxAge) {
        this.forgetConversation();
        return null;
      }
      return id;
    }

    rememberConversation(id) {
      this.conversationId = id;
      storageSet(STORAGE_ID, id);
      storageSet(STORAGE_AT, String(Date.now()));
    }

    forgetConversation() {
      this.conversationId = null;
      storageSet(STORAGE_ID, null);
      storageSet(STORAGE_AT, null);
      storageSet(STORAGE_RESOLVED, null);
      storageSet(STORAGE_VERIFIED, null);
    }

    resolutionAsked() {
      return this.conversationId !== null && storageGet(STORAGE_RESOLVED) === this.conversationId;
    }

    setIdentity(verified, firstName, signedIn) {
      this.verified = Boolean(verified);
      this.firstName = firstName || null;
      this.signedIn = Boolean(signedIn);
      if (this.verified && !this.signedIn && this.conversationId) storageSet(STORAGE_VERIFIED, this.conversationId);
      let who = '';
      if (this.verified) {
        who = this.firstName ? `${this.signedIn ? 'Signed in' : 'Verified'} · ${this.firstName}` : this.signedIn ? 'Signed in' : 'Verified';
      }
      this.whoEl.textContent = who;
    }

    /** A returning conversation id: draw its history before the first turn. */
    async restore() {
      const id = this.storedConversationId();
      if (!id) return;
      this.conversationId = id;
      try {
        const response = await fetch(`${PROXY_BASE}/conversation/${encodeURIComponent(id)}`, { headers: { Accept: 'application/json' } });
        // A new chat started while the history was loading: that history is not this conversation's.
        if (this.conversationId !== id) return;
        if (response.status === 404) {
          /* "Conversation not found" means the id is not ours to read (a different login, or a row that was
             never written): start clean. Any other 404 (the platform flag off, Shopify's own not-found page)
             says nothing about the conversation, so the id is kept for when the routes come up. */
          const payload = await response.json().catch(() => null);
          const gone = payload && typeof payload.error === 'string' && payload.error.startsWith('Conversation');
          if (gone) this.forgetConversation();
          return;
        }
        if (!response.ok) return;
        const payload = await response.json();
        const data = payload && payload.data;
        if (!data || !Array.isArray(data.messages)) return;
        this.setIdentity(data.verified, data.first_name, Boolean(data.signed_in));
        data.messages.forEach((message) => this.drawRestoredMessage(message));
        if (data.messages.length > 0) this.grown = true;
        this.turnIndex = data.messages.filter((message) => message.role === 'user').length;
        this.answers = data.messages.filter((message) => message.role === 'assistant').length;
        this.syncNewChatButton();
        this.scrollToEnd(true);
      } catch {
        /* offline or blocked: the conversation continues without its history */
      }
    }

    drawRestoredMessage(message) {
      if (message.role === 'user') {
        this.appendUser(message.content);
        return;
      }
      const el = this.appendAssistant();
      el.querySelector('.ami-thinking').remove();
      this.stopThinking();
      this.setText(el, message.content);
      (message.blocks || []).forEach((block) => this.appendBlock(el, block, { restored: true }));
      this.finishAssistant(el, message.id, message.rating);
    }

    /* ---- message DOM ---- */

    note(text) {
      const el = document.createElement('p');
      el.className = 'ami-chat__note';
      el.textContent = text;
      this.logEl.appendChild(el);
      this.scrollToEnd();
    }

    appendUser(text) {
      const el = document.createElement('div');
      el.className = 'ami-msg ami-msg--user';
      const bubble = document.createElement('div');
      bubble.className = 'ami-msg__bubble';
      bubble.textContent = text;
      el.appendChild(bubble);
      this.logEl.appendChild(el);
      this.scrollToEnd();
      return el;
    }

    appendAssistant() {
      const el = document.createElement('div');
      el.className = 'ami-msg ami-msg--ami';
      el.innerHTML =
        '<div class="ami-tools" data-ami-tools hidden></div>' +
        `<div class="ami-thinking">${SPINNER}<span data-ami-thinking>${escapeHtml(pickThinking(null))}</span></div>` +
        '<div class="ami-msg__text" data-ami-text hidden></div>' +
        '<div class="ami-msg__blocks" data-ami-blocks></div>' +
        '<div class="ami-sources" data-ami-sources hidden></div>' +
        '<div class="ami-thumbs" data-ami-thumbs hidden></div>';
      this.logEl.appendChild(el);
      this.startThinking(el);
      this.scrollToEnd();
      return el;
    }

    /* The pill rotates its phrase until the first tool pill, word or block arrives (each of those removes or
       outranks it), and settles on "Nearly there…" once the wait is genuinely long. */
    startThinking(el) {
      this.stopThinking();
      const startedAt = Date.now();
      this.thinkingTimer = window.setInterval(() => {
        const span = el.querySelector('[data-ami-thinking]');
        const toolsEl = el.querySelector('[data-ami-tools]');
        if (!span || !el.isConnected || (toolsEl && !toolsEl.hidden)) {
          this.stopThinking();
          return;
        }
        if (Date.now() - startedAt >= THINKING_LATE_MS) {
          span.textContent = THINKING_LATE;
          this.stopThinking();
          return;
        }
        span.textContent = pickThinking(span.textContent);
      }, THINKING_ROTATE_MS);
    }

    stopThinking() {
      window.clearInterval(this.thinkingTimer);
      this.thinkingTimer = 0;
    }

    setText(el, raw) {
      const textEl = el.querySelector('[data-ami-text]');
      const html = renderMarkdown(raw);
      textEl.hidden = html.length === 0;
      textEl.innerHTML = html;
    }

    appendBlock(el, block, options) {
      const rendered = Blocks.render(block);
      if (!rendered) return;
      el.querySelector('[data-ami-blocks]').appendChild(rendered);
      el.dataset.hasProducts = ['product_grid', 'product_card', 'comparison', 'bundle'].includes(block.kind) ? 'true' : el.dataset.hasProducts || '';
      if (!(options && options.restored)) this.track('Ami Block Rendered', { kind: block.kind });
      this.scrollToEnd();
    }

    setTools(el, tools) {
      const toolsEl = el.querySelector('[data-ami-tools]');
      const visible = tools.filter((tool) => !SILENT_TOOLS.has(tool.name));
      toolsEl.hidden = visible.length === 0;
      toolsEl.innerHTML = visible
        .map((tool) => {
          const label = (Object.prototype.hasOwnProperty.call(TOOL_LABELS, tool.name) && TOOL_LABELS[tool.name]) || 'Looking that up';
          const icon = tool.state === 'running' ? SPINNER : tool.state === 'failed' ? ICONS.alert : ICONS.checkCircle;
          return `<span class="ami-tool" data-state="${tool.state}">${icon}<span>${escapeHtml(label)}${tool.state === 'running' ? '…' : ''}</span></span>`;
        })
        .join('');
    }

    setSources(el, sources) {
      if (!sources || el.dataset.hasProducts === 'true') return;
      const chips = [];
      (sources.articles || []).slice(0, 3).forEach((article) => {
        chips.push(`<a class="dsc-chip" href="${HELP_HOST}/articles/${encodeURIComponent(article.slug)}" target="_blank" rel="noopener" data-ami-target="article">${escapeHtml(article.title)}</a>`);
      });
      (sources.products || []).slice(0, 4).forEach((product) => {
        chips.push(`<a class="dsc-chip" href="/products/${encodeURIComponent(product.handle)}" data-ami-target="product">${escapeHtml(product.title)}</a>`);
      });
      if (chips.length === 0) return;
      const sourcesEl = el.querySelector('[data-ami-sources]');
      const label = (sources.products || []).length ? 'Related' : 'Related articles';
      sourcesEl.innerHTML = `<span class="ami-sources__label">${label}</span>` + chips.join('');
      sourcesEl.hidden = false;
    }

    finishAssistant(el, messageId, rating) {
      el.classList.remove('ami-msg--streaming');
      const thinking = el.querySelector('.ami-thinking');
      if (thinking) thinking.remove();
      if (!messageId) return;
      const thumbs = el.querySelector('[data-ami-thumbs]');
      thumbs.innerHTML =
        `<button type="button" class="ami-thumbs__btn" data-ami-thumb="1" data-ami-message="${escapeHtml(messageId)}" aria-label="Helpful" aria-pressed="${rating === 1 ? 'true' : 'false'}">${ICONS.thumbUp}</button>` +
        `<button type="button" class="ami-thumbs__btn" data-ami-thumb="-1" data-ami-message="${escapeHtml(messageId)}" aria-label="Not helpful" aria-pressed="${rating === -1 ? 'true' : 'false'}">${ICONS.thumbDown}</button>`;
      thumbs.hidden = false;
    }

    appendVerification(el, question) {
      const form = document.createElement('form');
      form.className = 'ami-verify';
      form.setAttribute('aria-label', 'Verify your order');
      form.innerHTML =
        '<div class="ami-verify__title">Verify it is your order</div>' +
        '<div class="ami-verify__hint">The email you used at checkout and an order number, both from your confirmation email. Then Ami can see your orders, downloads and licences.</div>' +
        '<div class="input"><input id="ami-verify-email" class="input__field" type="email" name="email" required autocomplete="email"><label class="input__label" for="ami-verify-email">Email address</label></div>' +
        '<div class="ami-verify__row"><div class="input"><input id="ami-verify-order" class="input__field" type="text" name="order_number" required autocomplete="off" maxlength="20"><label class="input__label" for="ami-verify-order">Order number, like #1234</label></div><button type="submit" class="button button--primary button--small">Verify</button></div>' +
        '<p class="ami-verify__error" data-ami-verify-error hidden></p>';
      // Focal floats the label off :focus-within and .is-filled (theme.js does the same on its own forms).
      form.querySelectorAll('.input__field').forEach((field) => {
        field.addEventListener('input', () => field.classList.toggle('is-filled', field.value !== ''));
      });
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.verify(form, el, question);
      });
      el.querySelector('[data-ami-blocks]').appendChild(form);
      this.scrollToEnd();
    }

    appendEscalation(el, frame) {
      const card = document.createElement('div');
      card.className = 'ami-escalation';
      card.setAttribute('role', 'status');
      const reason = String(frame.reason || '').replace(/_/g, ' ');
      card.innerHTML = `${ICONS.checkCircle}<div><strong>Support thread created</strong>Our team will follow up by email${frame.email ? ` at ${escapeHtml(frame.email)}` : ''}${reason ? ` about ${escapeHtml(reason)}` : ''}.</div>`;
      el.querySelector('[data-ami-blocks]').appendChild(card);
    }

    appendError(el, text) {
      const card = document.createElement('div');
      card.className = 'ami-error';
      card.innerHTML = `${escapeHtml(text)} <a href="${HELP_HOST}/contact?fromChat=true" target="_blank" rel="noopener">Contact support</a>`;
      el.querySelector('[data-ami-blocks]').appendChild(card);
      const thinking = el.querySelector('.ami-thinking');
      if (thinking) thinking.remove();
      this.scrollToEnd();
    }

    scrollToEnd(force) {
      if (!force && !this.stick) return;
      this.logEl.scrollTop = this.logEl.scrollHeight;
    }

    /* ---- context (§4.4): the page from Liquid, the cart from /cart.js, outcomes since the last turn ---- */

    pageContext() {
      try {
        const raw = railAttr('data-ami-context');
        const parsed = raw ? JSON.parse(raw) : {};
        const page = {};
        // Bounds mirror validators/amiStorefrontValidators.ts: an overflow is a 400 on every turn.
        const limits = {
          template: 60,
          product_handle: 255,
          product_shopify_id: 20,
          collection_handle: 255,
          collection_title: 200,
          article_handle: 255,
          url_path: 500,
        };
        Object.keys(limits).forEach((key) => {
          if (typeof parsed[key] === 'string' && parsed[key]) page[key] = parsed[key].slice(0, limits[key]);
        });
        // The first page of a collection, as Liquid rendered it (24 handles at most, the validator's cap).
        if (Array.isArray(parsed.collection_products)) {
          const handles = parsed.collection_products.filter((h) => typeof h === 'string' && h).slice(0, 24);
          if (handles.length) page.collection_products = handles.map((h) => h.slice(0, 255));
        }
        if (!page.url_path) page.url_path = location.pathname.slice(0, 500);
        if (page.template === 'search') Object.assign(page, this.searchContext());
        return page;
      } catch {
        return { url_path: location.pathname.slice(0, 500) };
      }
    }

    /* The search page's state is its URL (discover.js writes every refinement back with pushState), read at send
       time so Ami sees the query and filters as they are now, not as the page loaded. */
    searchContext() {
      const params = new URLSearchParams(location.search);
      const query = (params.get('q') || '').trim();
      params.delete('q');
      params.delete('src');
      params.delete('image');
      const filters = params.toString();
      const out = {};
      if (query) out.search_query = query.slice(0, 200);
      if (filters) {
        let readable = filters;
        try {
          readable = decodeURIComponent(filters);
        } catch {
          /* a malformed escape in a hand-edited URL: send it encoded */
        }
        out.search_filters = readable.slice(0, 500);
      }
      return out;
    }

    async cartContext() {
      try {
        const response = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
        if (!response.ok) return undefined;
        const cart = await response.json();
        return {
          item_count: cart.item_count,
          subtotal: (cart.items_subtotal_price ?? cart.total_price) / 100,
          currency: cart.currency,
          items: (cart.items || []).slice(0, 50).map((item) => ({
            variant_id: String(item.variant_id),
            product_handle: String(item.handle || ''),
            title: String(item.product_title || item.title || '').slice(0, 300),
            license_title: item.variant_title ? String(item.variant_title).slice(0, 200) : null,
            quantity: Math.min(999, Math.max(1, Number(item.quantity) || 1)),
            price: item.price / 100,
          })),
        };
      } catch {
        return undefined;
      }
    }

    /** Record an action outcome: sent to /ami/event now, and carried on the next turn so Ami can refer to it. */
    recordEvent(type, detail) {
      const event = { type, at: new Date().toISOString(), detail: detail || {} };
      this.pendingEvents.push(event);
      if (this.pendingEvents.length > MAX_CLIENT_EVENTS) this.pendingEvents.shift();
      if (!this.conversationId || isDesignMode() || corsPinned()) return;
      fetch(`${PROXY_BASE}/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: this.conversationId, event }),
        keepalive: true,
      }).catch(() => {});
    }

    /* ---- a turn ---- */

    async send(text, options) {
      const message = String(text || '').trim().slice(0, MESSAGE_MAX);
      if (!message || this.busy) return;
      if (isDesignMode()) return;
      this.busy = true;
      this.sendEl.disabled = true;
      this.inputEl.disabled = true;
      // A reset mid-stream would let this turn's `done` frame re-adopt the old conversation id.
      this.newEl.disabled = true;
      window.clearTimeout(this.idleTimer);
      this.stick = true;
      this.lastQuestion = message;
      this.dropNewChatConfirm();
      this.dropStarters();
      this.grown = true; // the card takes its full height now and keeps it for the session
      this.setCompact(false);
      if (!(options && options.reuseUserBubble)) this.appendUser(message);
      this.turnIndex += 1;
      this.syncNewChatButton();
      this.track('Ami Message Sent', { turn_index: this.turnIndex, length: message.length });
      const el = this.appendAssistant();
      el.classList.add('ami-msg--streaming');
      const turn = { el, raw: '', tools: [], messageId: null, verification: false, done: false, failed: false, frames: 0, rendered: false };
      try {
        await this.runTurn(message, turn);
      } catch {
        if (!turn.done) {
          turn.failed = true;
          this.appendError(el, 'Ami could not answer that. Try again in a moment.');
        }
      } finally {
        // A stream that closed cleanly without `done` (an edge timeout on a long tool turn) must not leave the
        // spinner up with nothing to do next.
        if (!turn.done && !turn.failed) this.appendError(el, 'Ami could not finish that answer. Try again.');
        this.busy = false;
        this.sendEl.disabled = false;
        this.inputEl.disabled = false;
        this.newEl.disabled = false;
        this.stopThinking();
        if (this.isOpen) this.focusInput();
        if (turn.done) {
          this.answers += 1;
          this.armIdleResolution();
        }
        if (this.conversationId) this.rememberConversation(this.conversationId);
      }
    }

    async runTurn(message, turn) {
      /* The id is minted here when there is none, as the help site does: the backend adopts a client id it
         has not seen, and hands back a different one only when this one is not ours to use (the `conversation`
         frame, onConversation). Minting client-side also keeps a conversation whole on a backend that does not
         yet send that frame. */
      if (!this.conversationId && window.crypto && typeof window.crypto.randomUUID === 'function') {
        this.rememberConversation(window.crypto.randomUUID());
      }
      const events = this.pendingEvents.splice(0, MAX_CLIENT_EVENTS);
      const ids = posthogIds();
      const body = {
        message,
        conversation_id: this.conversationId || undefined,
        context: { page: this.pageContext(), cart: await this.cartContext(), client_events: events.length ? events : undefined },
        honeypot: this.querySelector('[data-ami-hp]').value || undefined,
        posthog_distinct_id: ids.distinct,
        posthog_session_id: ids.session,
      };
      const onFrame = (frame) => this.onFrame(turn, frame);
      if (!corsPinned()) {
        const result = await this.streamProxy(body, onFrame);
        if (result === 'ok') return;
        // A stream that drew something and then failed is not retried on another route: the answer would
        // land twice under the same bubble.
        if (result === 'fatal' || turn.rendered) throw new Error('proxy stream failed');
        // 'degrade': the proxy buffers or the platform is off — use the help site's routes for a while.
        pinCors();
        this.pendingEvents.unshift(...events);
      }
      await this.streamCors(message, onFrame, turn);
    }

    /** Returns 'ok', 'degrade' (try the CORS routes) or 'fatal'. No `Accept: text/event-stream` on the request:
        the server's content-type decides, and `shopify theme dev` answers any request carrying that header with
        its own hot-reload stream, whose first frame is also `{type: 'open'}`. */
    async streamProxy(body, onFrame) {
      const controller = new AbortController();
      let opened = false;
      const timer = window.setTimeout(() => {
        if (!opened) controller.abort();
      }, OPEN_TIMEOUT_MS);
      try {
        const response = await fetch(`${PROXY_BASE}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        if (response.status === 404) return 'degrade';
        if (!response.ok || !response.body) return 'fatal';
        await readFrames(response, (frame) => {
          if (frame.type === 'open') opened = true;
          onFrame(frame);
        });
        return opened ? 'ok' : 'degrade';
      } catch (error) {
        return !opened && error && error.name === 'AbortError' ? 'degrade' : 'fatal';
      } finally {
        window.clearTimeout(timer);
      }
    }

    apiHost() {
      return railAttr('data-ami-api-host') || DEFAULT_API_HOST;
    }

    /** The help site's routes: same backend, same frames, no proxy — and no storefront context or login. */
    async streamCors(message, onFrame, turn) {
      const ids = posthogIds();
      const payload = {
        message,
        conversationId: this.conversationId || undefined,
        honeypot: this.querySelector('[data-ami-hp]').value || undefined,
        posthog_distinct_id: ids.distinct,
        posthog_session_id: ids.session,
      };
      const host = this.apiHost();
      try {
        const response = await fetch(`${host}/api/help/chat/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (response.ok && response.body) {
          await readFrames(response, onFrame);
          if (turn.done) return;
        }
      } catch {
        /* fall through to the JSON route */
      }
      // Anything already on screen from a stream that died mid-answer: a JSON retry would draw it twice.
      if (turn.rendered) throw new Error('stream ended early');
      const response = await fetch(`${host}/api/help/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || !result || !result.success) throw new Error('chat failed');
      onFrame({ type: 'delta', content: result.data.response || '' });
      if (result.data.sources) onFrame({ type: 'sources', sources: result.data.sources });
      onFrame({ type: 'done', conversation_id: this.conversationId, message_id: null });
    }

    onFrame(turn, frame) {
      turn.frames += 1;
      const { el } = turn;
      switch (frame.type) {
        case 'open':
        case 'ping':
          return;
        case 'conversation':
          return this.onConversation(turn, frame);
        case 'delta':
          turn.raw += frame.content;
          turn.rendered = true;
          return this.scheduleText(turn);
        case 'tool_start':
          turn.tools.push({ id: frame.id, name: frame.tool, state: 'running' });
          return this.setTools(el, turn.tools);
        case 'tool_complete': {
          const tool = turn.tools.find((item) => item.id === frame.id);
          if (tool) tool.state = frame.success ? 'complete' : 'failed';
          return this.setTools(el, turn.tools);
        }
        case 'block':
          turn.rendered = true;
          return this.appendBlock(el, frame.block);
        case 'verification_required':
          turn.verification = true;
          return;
        case 'escalation':
          turn.rendered = true;
          return this.appendEscalation(el, frame);
        case 'sources':
          return this.setSources(el, frame.sources);
        case 'warning':
          return this.appendLongNote();
        case 'done':
          return this.onDone(turn, frame);
        case 'error':
          turn.done = true;
          return this.appendError(el, frame.error || 'Ami could not answer that.');
        default:
          return;
      }
    }

    onConversation(turn, frame) {
      const previous = this.conversationId;
      if (previous && frame.id !== previous) {
        /* The server started a fresh conversation (a login-tied id presented anonymously, or an expired one):
           what was drawn before this turn is not this conversation's history. */
        const keep = new Set([turn.el, turn.el.previousElementSibling]);
        Array.from(this.logEl.children).forEach((child) => {
          if (!keep.has(child)) child.remove();
        });
        this.turnIndex = 1;
        this.answers = 0;
        this.resolutionShown = false;
        storageSet(STORAGE_RESOLVED, null);
      }
      this.rememberConversation(frame.id);
      this.setIdentity(frame.verified, frame.first_name, frame.signed_in);
    }

    scheduleText(turn) {
      if (turn.textRaf) return;
      turn.textRaf = window.requestAnimationFrame(() => {
        turn.textRaf = 0;
        const thinking = turn.el.querySelector('.ami-thinking');
        if (thinking) thinking.remove();
        this.setText(turn.el, turn.raw);
        this.scrollToEnd();
      });
    }

    onDone(turn, frame) {
      turn.done = true;
      if (turn.textRaf) {
        window.cancelAnimationFrame(turn.textRaf);
        turn.textRaf = 0;
      }
      this.setText(turn.el, turn.raw);
      // The rendered text, not the markdown source, so the reader hears words rather than asterisks.
      if (this.announceEl) this.announceEl.textContent = turn.el.querySelector('[data-ami-text]').textContent.slice(0, 2000);
      if (frame.conversation_id && frame.conversation_id !== this.conversationId) this.rememberConversation(frame.conversation_id);
      turn.messageId = frame.message_id || null;
      this.finishAssistant(turn.el, turn.messageId, null);
      // Only anonymous visitors verify by email and order number; a signed-in customer never sees the form.
      if (turn.verification && !this.verified) this.appendVerification(turn.el, this.lastQuestion);
      this.scrollToEnd();
    }

    appendLongNote() {
      if (this.longNoted) return;
      this.longNoted = true;
      const el = document.createElement('div');
      el.className = 'ami-resolution';
      el.innerHTML = '<span class="ami-resolution__q">This chat is getting long. A new one will get better answers.</span><button type="button" class="button button--outline button--small" data-ami-new>Start a new chat</button>';
      this.logEl.appendChild(el);
    }

    /* ---- new chat: the head button and the "getting long" note both land here ---- */

    /** The head button shows once there is a conversation to leave (a sent turn, or restored history). */
    syncNewChatButton() {
      this.newEl.hidden = this.turnIndex === 0 && this.answers === 0;
    }

    /** The "start a new chat?" card must not outlive the decision: a sent turn, a close, or the resolution
        line all answer it with no. */
    dropNewChatConfirm() {
      const pending = this.logEl.querySelector('[data-ami-new-confirm]');
      if (pending) pending.remove();
    }

    /* An anonymous visitor who verified by email and order number loses that verification with the
       conversation (the id is its only bearer), so they get one inline question first. A signed-in customer
       is verified by login on every conversation and nothing is lost, so they go straight through. */
    requestNewChat(from) {
      if (this.busy) return;
      if (this.turnIndex === 0 && this.answers === 0) return;
      if (!(this.verified && !this.signedIn)) {
        this.startNewConversation(from);
        return;
      }
      const existing = this.logEl.querySelector('[data-ami-new-confirm]');
      if (existing) {
        this.scrollToEnd(true);
        return;
      }
      const el = document.createElement('div');
      el.className = 'ami-resolution';
      el.setAttribute('data-ami-new-confirm', '');
      el.setAttribute('data-ami-from', from || 'header');
      el.innerHTML =
        '<span class="ami-resolution__q">Start a new chat? Your order verification will reset.</span>' +
        '<button type="button" class="button button--outline button--small" data-ami-new-confirm-yes>Start new</button>' +
        '<button type="button" class="button button--outline button--small" data-ami-new-confirm-no>Keep this one</button>';
      this.logEl.appendChild(el);
      this.scrollToEnd(true);
    }

    startNewConversation(from) {
      if (this.busy) return;
      window.clearTimeout(this.idleTimer);
      this.stopThinking();
      this.track('Ami New Chat', {
        from: from || 'header',
        turns: this.turnIndex,
        answers: this.answers,
        verified: this.verified,
        signed_in: this.signedIn,
        resolution_asked: this.resolutionAsked(),
      });
      this.forgetConversation();
      this.logEl.innerHTML = '';
      this.turnIndex = 0;
      this.answers = 0;
      this.resolutionShown = false;
      this.longNoted = false;
      this.turnsAtOpen = 0; // `Ami Closed` counts turns since open; the reset is the new zero
      if (this.announceEl) this.announceEl.textContent = 'New chat started.';
      // Login-tied identity survives the reset (the next turn's `conversation` frame restates it anyway).
      if (this.signedIn) this.setIdentity(true, this.firstName, true);
      else this.setIdentity(false, null, false);
      this.syncNewChatButton();
      this.drawStarters();
      this.focusInput();
    }

    /* ---- starters: the page's suggestion lines as chips while the log is empty ---- */

    /** The same lines the rail's capsule rolls (snippets/ami-suggestions.liquid), read off the rail. */
    starterLines() {
      return (railAttr('data-ami-suggestions') || '')
        .split('|')
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 6);
    }

    drawStarters() {
      this.dropStarters();
      if (this.logEl.childElementCount > 0) return; // restored history or a note: nothing to start
      // The name is a display nicety only (the rail's Liquid, or the identity a frame restated); the server
      // decides who is verified.
      const name = this.firstName || railAttr('data-ami-first-name') || '';
      const opener = `${name ? `Hi ${name}, ` : 'Hi, '}${STARTER_OPENER}`;
      const chips = this.starterLines()
        .map((line) => `<button type="button" class="dsc-chip" data-ami-ask="${escapeHtml(line)}">${escapeHtml(line)}</button>`)
        .join('');
      const el = document.createElement('div');
      el.className = 'ami-starters';
      el.setAttribute('data-ami-starters', '');
      el.innerHTML =
        '<span class="ami-starters__mark" aria-hidden="true">✦</span>' +
        `<div class="ami-msg ami-msg--ami"><div class="ami-msg__text">${escapeHtml(opener)}</div></div>` +
        (chips ? `<div class="ami-starters__chips">${chips}</div>` : '');
      this.logEl.appendChild(el);
      this.setCompact(!this.grown);
      this.scrollToEnd(true);
    }

    setCompact(on) {
      if (this.compact === on) return;
      this.compact = on;
      this.classList.toggle('ami-chat--compact', on);
      this.schedulePosition();
    }

    dropStarters() {
      const el = this.logEl.querySelector('[data-ami-starters]');
      if (el) el.remove();
      if (el) this.setCompact(false);
    }

    /* ---- verification (anonymous only) ---- */

    /** Same-origin through the proxy (POST /ami/verify, under the ownership rule) whenever the proxy is the
        transport; the help site's CORS route is the degrade, as for the chat itself. */
    async postVerification(body) {
      const init = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
      if (!corsPinned()) {
        try {
          const proxied = await fetch(`${PROXY_BASE}/verify`, init);
          if (proxied.status !== 404) return proxied;
        } catch {
          /* the proxy is unreachable: the help site's route answers the same shape */
        }
      }
      return fetch(`${this.apiHost()}/api/help/chat/verify`, init);
    }

    async verify(form, el, question) {
      const errorEl = form.querySelector('[data-ami-verify-error]');
      const button = form.querySelector('button[type="submit"]');
      const email = form.elements.email.value.trim();
      const orderNumber = form.elements.order_number.value.trim();
      if (!email || !orderNumber || !this.conversationId) return;
      button.disabled = true;
      errorEl.hidden = true;
      const forConversation = this.conversationId;
      try {
        const response = await this.postVerification({ email, order_number: orderNumber, conversation_id: forConversation });
        const payload = await response.json().catch(() => null);
        // A new chat started while this was in flight: the verification belongs to the conversation that
        // was abandoned, and the question it was going to answer went with it.
        if (this.conversationId !== forConversation) return;
        if (response.status === 429) throw new Error('Too many attempts. Wait a moment and try again.');
        if (!response.ok || !payload || !payload.success) throw new Error((payload && payload.error) || 'Verification failed. Try again.');
        if (!payload.data.verified) throw new Error(payload.data.error || 'That order was not found.');
        this.setIdentity(true, payload.data.customer_first_name, false);
        // Ami now has the account tools: answer the question that needed them, under the same bubble.
        el.remove();
        if (question) this.send(question, { reuseUserBubble: true });
      } catch (error) {
        errorEl.textContent = error instanceof Error ? error.message : 'Verification failed. Try again.';
        errorEl.hidden = false;
        button.disabled = false;
      }
    }

    /* ---- feedback (§4.8) ---- */

    async rate(button) {
      const messageId = button.getAttribute('data-ami-message');
      const value = Number(button.getAttribute('data-ami-thumb'));
      const wasPressed = button.getAttribute('aria-pressed') === 'true';
      const rating = wasPressed ? 0 : value;
      const group = button.parentElement;
      group.querySelectorAll('[data-ami-thumb]').forEach((other) => other.setAttribute('aria-pressed', 'false'));
      if (!wasPressed) button.setAttribute('aria-pressed', 'true');
      this.track('Ami Rated', { value: rating });
      if (!this.conversationId || isDesignMode()) return;
      try {
        await fetch(`${PROXY_BASE}/rate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversation_id: this.conversationId, message_id: messageId, rating }),
        });
      } catch {
        /* the rating stays as shown; the next one retries */
      }
    }

    armIdleResolution() {
      window.clearTimeout(this.idleTimer);
      if (this.resolutionShown || this.resolutionAsked()) return;
      this.idleTimer = window.setTimeout(() => {
        if (this.isOpen && !this.busy) this.showResolution();
      }, IDLE_RESOLUTION_MS);
    }

    showResolution() {
      if (this.resolutionShown) return;
      this.resolutionShown = true;
      this.dropNewChatConfirm(); // one open question in the log at a time
      const el = document.createElement('div');
      el.className = 'ami-resolution';
      el.setAttribute('data-ami-resolution-card', '');
      el.innerHTML =
        '<span class="ami-resolution__q">Did that sort it out?</span>' +
        '<button type="button" class="button button--outline button--small" data-ami-resolution="resolved">Yes</button>' +
        '<button type="button" class="button button--outline button--small" data-ami-resolution="unresolved">Not yet</button>';
      this.logEl.appendChild(el);
      this.scrollToEnd(true);
    }

    async resolve(button) {
      const card = button.closest('[data-ami-resolution-card]');
      const answer = button.getAttribute('data-ami-resolution');
      this.track('Ami Resolved', { answer });
      if (this.conversationId) storageSet(STORAGE_RESOLVED, this.conversationId);
      if (answer === 'resolved') {
        card.innerHTML = '<span class="ami-resolution__done">Glad that helped.</span>';
        this.postResolution('resolved');
        return;
      }
      // Recorded now, so an abandoned note still counts as unresolved; the note updates the same row.
      this.postResolution('unresolved');
      card.innerHTML =
        '<span class="ami-resolution__q">Sorry about that. Tell us what was missing, or take it to the team.</span>' +
        '<div class="ami-resolution__note"><div class="input"><input id="ami-resolution-note" class="input__field" type="text" maxlength="500" data-ami-resolution-note><label class="input__label" for="ami-resolution-note">What was missing? (optional)</label></div><button type="button" class="button button--outline button--small" data-ami-resolution-send>Send</button></div>' +
        `<span class="ami-resolution__done"><a href="${HELP_HOST}/contact?fromChat=true" target="_blank" rel="noopener">Contact support</a></span>`;
      const note = card.querySelector('[data-ami-resolution-note]');
      note.addEventListener('input', () => note.classList.toggle('is-filled', note.value !== ''));
      note.focus();
    }

    sendResolutionNote(button) {
      const card = button.closest('[data-ami-resolution-card]');
      const note = card.querySelector('[data-ami-resolution-note]').value.trim();
      this.postResolution('unresolved', note);
      card.innerHTML = `<span class="ami-resolution__done">Thanks. Our team can pick it up: <a href="${HELP_HOST}/contact?fromChat=true" target="_blank" rel="noopener">Contact support</a></span>`;
    }

    postResolution(resolution, note) {
      if (!this.conversationId || isDesignMode()) return;
      fetch(`${PROXY_BASE}/resolution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: this.conversationId, resolution, note: note || undefined }),
        keepalive: true,
      }).catch(() => {});
    }

    /* ---- actions (§4.6): every one a button the customer taps, executed here, never on the server ---- */

    onLogClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const thumb = target.closest('.ami-thumbs__btn[data-ami-thumb]');
      if (thumb) return this.rate(thumb);
      const resolution = target.closest('[data-ami-resolution]');
      if (resolution) return this.resolve(resolution);
      const noteSend = target.closest('[data-ami-resolution-send]');
      if (noteSend) return this.sendResolutionNote(noteSend);
      if (target.closest('[data-ami-new]')) return this.requestNewChat('long_note');
      const confirmYes = target.closest('[data-ami-new-confirm-yes]');
      if (confirmYes) return this.startNewConversation(confirmYes.closest('[data-ami-new-confirm]').getAttribute('data-ami-from'));
      const confirmNo = target.closest('[data-ami-new-confirm-no]');
      if (confirmNo) {
        // The focused button goes with the card; below 1000px the card is a modal, so focus must stay inside.
        this.dropNewChatConfirm();
        this.focusInput();
        return undefined;
      }
      const ask = target.closest('[data-ami-ask]');
      if (ask) {
        const text = ask.getAttribute('data-ami-ask');
        if (ask.closest('[data-ami-starters]')) this.track('Ami Starter Clicked', { text, template: this.pageContext().template || null });
        return this.send(text);
      }
      const variant = target.closest('.ami-variant');
      if (variant) return this.selectVariant(variant);
      const addAll = target.closest('[data-ami-add-all]');
      if (addAll) return this.addAllToCart(addAll);
      const heart = target.closest('[data-wishlist-button]');
      if (heart) return this.onHeartTap(heart);
      const checkout = target.closest('[data-ami-checkout]');
      if (checkout) return this.openCheckout();
      const link = target.closest('a[href]');
      const block = link && link.closest('[data-ami-block]');
      if (link && block) {
        const kind = block.getAttribute('data-ami-block');
        const which = link.getAttribute('data-ami-target') || 'link';
        this.track('Ami Block Clicked', { kind, target: which });
        this.recordEvent('block_clicked', { kind, target: which, href: link.getAttribute('href').slice(0, 300) });
      }
      return undefined;
    }

    /* A licence row writes its variant into Focal's form and wakes the button. */
    selectVariant(row) {
      const box = row.closest('[data-ami-variants]');
      box.querySelectorAll('.ami-variant').forEach((other) => other.classList.toggle('is-selected', other === row));
      const input = row.querySelector('input');
      input.checked = true;
      const card = box.closest('.ami-block');
      const form = card && card.querySelector('[data-ami-buy]');
      if (!form) return;
      form.elements.id.value = input.value;
      const add = form.querySelector('[data-ami-add]');
      if (add) add.disabled = false;
    }

    /* Focal's ProductForm dispatched variant:added on the form: the cart, header count, mini cart and
       notification are already handled by the theme; only the outcome is recorded here. */
    onVariantAdded(form, product) {
      const variantId = String(form.elements.id.value || '');
      const status = form.closest('[data-ami-block]').querySelector('[data-ami-status]');
      if (status) {
        status.innerHTML = `In your cart. <a href="${escapeHtml(routes.cartUrl || '/cart')}">View cart</a> or <a href="/checkout" data-ami-checkout>check out</a>.`;
        status.hidden = false;
      }
      this.track('Ami Action', { action: 'add_to_cart', ok: true });
      this.recordEvent('added_to_cart', { variant_ids: variantId.slice(0, 300), count: 1, handle: String(product.handle).slice(0, 255) });
    }

    /* The snippet's own document listener toggles the wishlist after this runs (it is lifted with the first
       card render); record what it is about to do. If no card has rendered yet, toggle here instead. */
    onHeartTap(button) {
      if (!window.Wishlist) return;
      const id = button.getAttribute('data-product-id');
      const handle = button.getAttribute('data-product-handle') || '';
      const saving = !window.Wishlist.isWishlisted(id);
      if (!window.__wishlistButtonsInit) {
        window.Wishlist.toggle(id, handle);
        button.classList.toggle('is-wishlisted', saving);
      }
      this.track('Ami Action', { action: 'wishlist', ok: true });
      if (saving) this.recordEvent('wishlisted', { shopify_id: String(id), handle: String(handle) });
    }

    /* The bundle's one call for every item; Focal's form posts a single id, so this stays a JSON post that
       then hands the result to the same theme events (cart:updated, cart:refresh with the mini cart open). */
    async addAllToCart(button) {
      const ids = (button.getAttribute('data-ami-variant') || '').split(',').filter((id) => /^[1-9]\d*$/.test(id));
      if (ids.length === 0 || button.disabled) return;
      const all = true;
      const block = button.closest('[data-ami-block]');
      const status = block && block.querySelector('[data-ami-status]');
      const label = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';
      try {
        const response = await fetch(CART_ADD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })), sections: 'mini-cart' }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error((payload && (payload.description || payload.message)) || 'Could not add to cart');
        await this.refreshCart(payload && payload.sections);
        button.textContent = 'Added all';
        if (status) {
          status.innerHTML = `In your cart. <a href="${escapeHtml(routes.cartUrl || '/cart')}">View cart</a> or <a href="/checkout" data-ami-checkout>check out</a>.`;
          status.hidden = false;
        }
        this.track('Ami Action', { action: all ? 'add_all_to_cart' : 'add_to_cart', ok: true });
        this.recordEvent(all ? 'added_all_to_cart' : 'added_to_cart', { variant_ids: ids.join(',').slice(0, 300), count: ids.length });
        window.setTimeout(() => {
          button.disabled = false;
        }, 1500);
      } catch (error) {
        button.disabled = false;
        button.textContent = label;
        if (status) {
          status.textContent = error instanceof Error ? error.message : 'Could not add to cart.';
          status.hidden = false;
        }
        this.track('Ami Action', { action: all ? 'add_all_to_cart' : 'add_to_cart', ok: false });
        this.recordEvent('add_to_cart_failed', { variant_ids: ids.join(',').slice(0, 300) });
      }
    }

    /** Focal's own cart events (theme.js product-form), so the header count and the mini cart update. */
    async refreshCart(sections) {
      try {
        const response = await fetch(CART_URL);
        const cart = await response.json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        if (sections) cart.sections = sections;
        const openMiniCart = Boolean(window.themeVariables && window.themeVariables.settings.cartType === 'drawer');
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart } }));
      } catch {
        /* the cart is updated either way; only the header count is stale */
      }
    }

    openCheckout() {
      this.track('Ami Action', { action: 'checkout', ok: true });
      this.recordEvent('checkout_opened', {});
      // The anchor's own navigation carries the customer there.
    }
  }

  if (!window.customElements.get('ami-chat')) {
    window.customElements.define('ami-chat', AmiChat);
  }

  /* ---------- stylesheet, element and the public API ---------- */

  let stylesPromise = null;
  function ensureStyles() {
    if (stylesPromise) return stylesPromise;
    const href = railAttr('data-ami-css');
    if (!href) return (stylesPromise = Promise.reject(new Error('no rail configuration')));
    stylesPromise = new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      const done = () => resolve();
      link.addEventListener('load', done, { once: true });
      link.addEventListener('error', done, { once: true });
      window.setTimeout(done, 3000);
      document.head.appendChild(link);
    });
    return stylesPromise;
  }

  let element = null;
  async function ensureElement() {
    await ensureStyles();
    if (!element) {
      element = document.createElement('ami-chat');
      element.hidden = true; // the UA sheet hides it until open() has styles to show
      document.body.appendChild(element);
    }
    return element;
  }

  /* Without the rail (its section removed in the editor) there is no configuration to run on: the help
     centre is the honest fallback, as bottom-rail.js does before this script lands. */
  function openHelpCentre(question) {
    const url = question ? `${HELP_HOST}/?q=${encodeURIComponent(String(question).slice(0, MESSAGE_MAX))}` : HELP_HOST;
    window.open(url, '_blank', 'noopener');
  }

  window.AmiChat = {
    async open(question, options) {
      let el;
      try {
        el = await ensureElement();
      } catch {
        stylesPromise = null;
        return openHelpCentre(question);
      }
      return el.open(question, options);
    },
    close() {
      if (element) element.close({ fromUser: false });
    },
    isOpen() {
      return Boolean(element && element.isOpen);
    },
    async send(text) {
      let el;
      try {
        el = await ensureElement();
      } catch {
        stylesPromise = null;
        return openHelpCentre(text);
      }
      if (!el.isOpen) await el.open('', { surface: 'shim' });
      return el.send(text);
    },
  };

  /* The embed.js API, kept for discover.js and any third-party host. Callers may pass an event object rather
     than a string, so only real strings count. */
  window.Studio2amHelpWidget = {
    open(message) {
      const question = typeof message === 'string' ? message : '';
      return window.AmiChat.open(question, { surface: 'shim' });
    },
    close() {
      window.AmiChat.close();
    },
    isOpen() {
      return window.AmiChat.isOpen();
    },
  };
})();
